import axios from 'axios';
import i18n from '../i18n';

// In production, REACT_APP_API_URL='/api' (set during build)
// In development, REACT_APP_API_URL='http://localhost:8000'
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: `${API_URL}/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and language to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add language parameter to all requests
    const currentLanguage = i18n.language || 'en';
    config.params = {
      ...config.params,
      lang: currentLanguage
    };

    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Un equip pot perdre la sessió perquè algú ha entrat amb el mateix codi
      // en un altre ordinador. Es deixa dit a la pantalla d'entrada: un logout
      // sense explicació enmig d'un examen sembla una avaria de l'aplicació, i
      // el que ha passat té una causa i una solució (tornar-hi des d'aquí).
      if (error.response?.data?.detail === 'SESSIO_DESPLACADA') {
        sessionStorage.setItem('motiuSortida', 'sessioDesplacada');
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  }),
  register: (userData) => api.post('/auth/register', userData),
  // Entrada amb el codi imprès (equips), en dos temps.
  //
  // La comprovació NO emet cap token ni toca la sessió: només diu de qui és el
  // codi i si ja hi ha algú a dins. Ha de ser així perquè entrar de debò
  // desplaça la sessió oberta, i això no pot passar mentre encara es pot
  // prémer «cancel·la» —tombaria l'examen del company—.
  comprovaCodi: (code) => api.post('/auth/code/check', { code }),
  entraAmbCodi: (code) => api.post('/auth/code', { code }),
};

// Equips amb codi d'accés (instructor)
export const equipService = {
  getEquips: () => api.get('/equips/'),
  creaEquips: (quantitat) => api.post('/equips/', { quantitat }),
  // Dona un codi nou i invalida la targeta antiga. Tanca també la sessió oberta.
  regeneraCodi: (id) => api.post(`/equips/${id}/codi`),
  // Treu el codi: la targeta deixa d'obrir res i la feina es conserva. És el que
  // es fa en acabar l'examen, no pas eliminar l'equip.
  revocaCodi: (id) => api.delete(`/equips/${id}/codi`),
  // Treu l'equip de l'aplicació sense tocar-li la feina ni el codi.
  tancaSessio: (id) => api.delete(`/equips/${id}/sessio`),
  // Esborra el compte i tota la seva feina. Amb entregues tancades, el servidor
  // respon 409 i cal tornar-hi amb amb_entregues=true.
  elimina: (id, ambEntregues = false) =>
    api.delete(`/equips/${id}`, { params: ambEntregues ? { amb_entregues: true } : {} }),
};

// User services
export const userService = {
  getCurrentUser: () => api.get('/users/me'),
  getPendingUsers: () => api.get('/users/pending'),
  getAllUsers: () => api.get('/users/'),
  approveUser: (userId) => api.put(`/users/${userId}/approve`),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  updateProfile: (data) => api.put('/users/me', data),
  changePassword: (data) => api.put('/users/me/password', data),
  changeUserRole: (userId, role) => api.put(`/users/${userId}/role`, { role }),
  resetUserPassword: (userId) => api.put(`/users/${userId}/reset-password`),
};

// Mission services
export const missionService = {
  getMissions: () => api.get('/missions/'),
  getMission: (id) => api.get(`/missions/${id}`),
  // Versió sense traduir, amb el JSON {en, ca} sencer. La fa servir la pantalla
  // d'edició: qui escriu contingut bilingüe l'ha de veure sencer, i rebre'l ja
  // traduït és el que va acabar corrompent una hipòtesi a producció.
  getMissionForEditing: (id) => api.get(`/missions/${id}`, { params: { raw: true } }),
  createMission: (data) => api.post('/missions/', data),
  updateMission: (id, data) => api.put(`/missions/${id}`, data),
  deleteMission: (id) => api.delete(`/missions/${id}`),
  archiveMission: (id) => api.put(`/missions/${id}/archive`),
  unarchiveMission: (id) => api.put(`/missions/${id}/unarchive`),
  // Temps extra per a tota la classe alhora: allarga la finestra de la missió i
  // dona els minuts a tots els exercicis que segueixen vius.
  extendMission: (id, minutes) => api.post(`/missions/${id}/extend`, { minutes }),
  assignUser: (missionId, userId) => api.post(`/missions/${missionId}/assign/${userId}`),
  unassignUser: (missionId, userId) => api.delete(`/missions/${missionId}/assign/${userId}`),
  getAssignments: (missionId) => api.get(`/missions/${missionId}/assignments`),
  bulkAssignUsers: (missionId, userIds) => api.post(`/missions/${missionId}/assignments/bulk`, userIds),
  addHypothesis: (missionId, data) => api.post(`/missions/${missionId}/hypotheses`, data),
  updateHypothesis: (missionId, hypothesisId, data) => api.put(`/missions/${missionId}/hypotheses/${hypothesisId}`, data),
  deleteHypothesis: (missionId, hypothesisId) => api.delete(`/missions/${missionId}/hypotheses/${hypothesisId}`),
  addEvidence: (missionId, data) => api.post(`/missions/${missionId}/evidence`, data),
  updateEvidence: (missionId, evidenceId, data) => api.put(`/missions/${missionId}/evidence/${evidenceId}`, data),
  deleteEvidence: (missionId, evidenceId) => api.delete(`/missions/${missionId}/evidence/${evidenceId}`),
};

// Matrix services
export const matrixService = {
  createMatrix: (data) => api.post('/matrices/', data),
  getMyMatrices: () => api.get('/matrices/my'),
  getMissionMatrices: (missionId) => api.get(`/matrices/mission/${missionId}`),
  getMatrix: (id) => api.get(`/matrices/${id}`),
  updateMatrix: (id, data) => api.put(`/matrices/${id}`, data),
  updateCell: (matrixId, cellId, data) => api.put(`/matrices/${matrixId}/cells/${cellId}`, data),
  startMission: (id) => api.post(`/matrices/${id}/start`),  // New: Start mission timer
  submitMatrix: (id) => api.post(`/matrices/${id}/submit`),
  resetMatrix: (id) => api.post(`/matrices/${id}/reset`),
  // Senyal de vida: diu al servidor que el navegador hi és i en retorna el
  // rellotge, que és l'únic que compta. El client no decideix quan s'acaba.
  heartbeat: (id) => api.post(`/matrices/${id}/heartbeat`),
  reopenMatrix: (id) => api.post(`/matrices/${id}/reopen`),
  // Temps extra per a un exercici concret. Si ja havia vençut, el torna a obrir
  // amb exactament aquests minuts; si corria, els hi suma.
  extendMatrix: (id, minutes) => api.post(`/matrices/${id}/extend`, { minutes }),
  // Correcció de l'instructor: nota (0-10) i comentari, tots dos opcionals però
  // no alhora. L'alumne la veu a la seva entrega tancada.
  gradeMatrix: (id, data) => api.put(`/matrices/${id}/grade`, data),
  ungradeMatrix: (id) => api.delete(`/matrices/${id}/grade`),
  // User hypothesis management
  createUserHypothesis: (matrixId, data) => api.post(`/matrices/${matrixId}/hypotheses`, data),
  updateUserHypothesis: (matrixId, hypothesisId, data) => api.put(`/matrices/${matrixId}/hypotheses/${hypothesisId}`, data),
  deleteUserHypothesis: (matrixId, hypothesisId) => api.delete(`/matrices/${matrixId}/hypotheses/${hypothesisId}`),
};

/**
 * Avisa el servidor que la pestanya es tanca.
 *
 * No fa servir axios sinó fetch amb keepalive: quan es dispara l'esdeveniment
 * 'pagehide' el navegador ja està desmuntant la pàgina i mata les peticions
 * normals a mitges. keepalive li demana que l'acabi d'enviar igualment.
 * (navigator.sendBeacon també sobreviuria, però no deixa posar la capçalera
 * Authorization, i aquest endpoint demana autenticació.)
 *
 * El servidor NO congela de seguida en rebre-ho: espera un marge, perquè
 * recarregar la pàgina dispara exactament el mateix esdeveniment que tancar-la.
 */
export const leaveMatrixBeacon = (matrixId) => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    fetch(`${API_URL}/v1/matrices/${matrixId}/leaving`, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  } catch (error) {
    // La pestanya s'està tancant: no hi ha ningú a qui informar de l'error.
  }
};

export default api;